import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyGetChartImageQuery } from '@/store/api/BasicChart/chartImagesApi';
import Modal from '@/utils/modal.utils';

export default function ChartImages(): JSX.Element {
  const [params] = useSearchParams();
  const userId = params.get('userId');

  const chartIds = ['D1', 'D9'] as const;

  const [loading, setLoading] = useState(false);
  const [charts, setCharts] = useState<Record<string, any>>({});

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  const [fetchChartImage] = useLazyGetChartImageQuery();

  useEffect(() => {
    async function fetchCharts() {
      if (!userId) return;

      setLoading(true);
      try {
        const payload = {
          userId: String(userId),
          imageType: 'png' as const,
        };

        for (const chartId of chartIds) {
          const south = await fetchChartImage({
            ...payload,
            chartId,
            chartType: 'south',
          }).unwrap();

          const north = await fetchChartImage({
            ...payload,
            chartId,
            chartType: 'north',
          }).unwrap();

          setCharts((prev) => ({
            ...prev,
            [`${chartId}-South`]: south,
            [`${chartId}-North`]: north,
          }));
        }
      } finally {
        setLoading(false);
      }
    }

    fetchCharts();
  }, [userId, fetchChartImage]);

  const openModal = (clickedKey: string, title: string) => {
    const [chartId] = clickedKey.split('-');

    const south = charts[`${chartId}-South`]?.chartUrl;
    const north = charts[`${chartId}-North`]?.chartUrl;

    const images = [south, north].filter(Boolean) as string[];

    setModalImages(images);
    setSelectedTitle(title.replace(/(South|North)/, 'South Indian & North Indian'));
    setModalOpen(true);
  };

  return (
    <div className="rounded-xl">
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-4">
        <div className="col-span-2 border border-[#2DAAFD] p-6 rounded-xl">
          <h2 className="font-22 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
            RASHI CHART (SOUTH INDIAN & NORTH INDIAN)
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {charts['D1-South']?.chartUrl && (
              <div>
                <p className="font-semibold text-center font-spectral">RASHI CHART</p>
                <img
                  src={charts['D1-South'].chartUrl}
                  className="w-full rounded-md border p-[9px] border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D1-South', 'Rashi Chart (South)')}
                />
              </div>
            )}

            {charts['D1-North']?.chartUrl && (
              <div>
                <p className="font-semibold text-center font-spectral">RASHI CHART</p>
                <img
                  src={charts['D1-North'].chartUrl}
                  className="w-full rounded-md border border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D1-North', 'Rashi Chart (North)')}
                />
              </div>
            )}
          </div>
        </div>

        <div className="col-span-2 border border-[#2DAAFD] p-6 rounded-xl w-full">
          <h2 className="font-24 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
            NAVAMSA CHART (SOUTH INDIAN & NORTH INDIAN)
          </h2>

          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            {charts['D9-South']?.chartUrl && (
              <div>
                <p className="font-semibold text-center font-spectral">NAVAMSA CHART</p>
                <img
                  src={charts['D9-South'].chartUrl}
                  className="w-full rounded-md border p-[9px] border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D9-South', 'Navamsa Chart (South)')}
                />
              </div>
            )}

            {charts['D9-North']?.chartUrl && (
              <div>
                <p className="font-semibold text-center font-spectral">NAVAMSA CHART</p>
                <img
                  src={charts['D9-North'].chartUrl}
                  className="w-full rounded-md border border-[#2DAAFD] cursor-pointer"
                  onClick={() => openModal('D9-North', 'Navamsa Chart (North)')}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-3xl"
      >
        <div className="flex flex-col gap-4 md:flex-row">
          {modalImages.map((img, i) => (
            <img
              key={i}
              src={img}
              className={`w-full rounded-md ${i === 1 ? 'scale-[1.05]' : ''}`}
            />
          ))}
        </div>
      </Modal>
    </div>
  );
}
